import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import punycode from 'punycode';
import { checkExistingEmail } from '../../services/companyService.js';

const CompanyPreview = ({ company, onConfirm, onEdit, loading }) => {
    const [customDomain, setCustomDomain] = useState('');
    const [isCorrectWebsite, setIsCorrectWebsite] = useState(true);
    const [hasNoWebsite, setHasNoWebsite] = useState(false);
    const [urlError, setUrlError] = useState('');
    const [password, setPassword] = useState('');
    const [companyEmail, setCompanyEmail] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [error, setError] = useState('');
    const [acceptedPolicy, setAcceptedPolicy] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);

    const validateUrl = (url) => {
        try {
            // Add https:// if no protocol is specified
            const urlToTest = url.match(/^https?:\/\//) ? url : `https://${url}`;
            new URL(urlToTest);
            
            // Check if it's a valid domain format
            const domainRegex = /^https?:\/\/[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+/;
            if (!domainRegex.test(urlToTest)) {
                return false;
            }
            
            return urlToTest;
        } catch {
            return false;
        }
    };

    const decodePunycode = (url) => {
        try {
            const urlObj = new URL(url);
            const decodedHostname = punycode.toUnicode(urlObj.hostname);
            return url.replace(urlObj.hostname, decodedHostname);
        } catch (e) {
            return url;
        }
    };

    const handleUrlChange = (e) => {
        const url = e.target.value;
        setCustomDomain(url);
        
        if (url && !validateUrl(url)) {
            setUrlError('Indtast venligst en gyldig webadresse (f.eks. www.minhjemmeside.dk)');
        } else {
            setUrlError('');
        }
    };

    const validatePassword = () => {
        if (password.length < 6) {
            setPasswordError('Password skal være mindst 6 tegn');
            return false;
        }
        if (password !== confirmPassword) {
            setPasswordError('Passwords matcher ikke');
            return false;
        }
        setPasswordError('');
        return true;
    };

    const handleConfirm = async () => {
        if (customDomain && !validateUrl(customDomain)) {
            setUrlError('Indtast venligst en gyldig webadresse');
            return;
        }
    
        if (!validatePassword()) {
            return;
        }
    
        // Add email validation
        if (!companyEmail) {
            setError('Email er påkrævet');
            return;
        }
    
        if (!acceptedPolicy) {
            setError('Du skal acceptere privatlivspolitikken for at fortsætte');
            return;
        }
    
        try {
            // Check if email exists before attempting to create user
            const emailExists = await checkExistingEmail(companyEmail);
            if (emailExists) {
                setError('Denne email er allerede registreret. Brug venligst en anden email.');
                return;
            }
    
            const websiteInfo = hasNoWebsite ? null : 
                isCorrectWebsite ? company.existingWebsite : 
                customDomain ? {
                    url: validateUrl(customDomain),
                    title: '',
                    description: ''
                } : null;
    
            // Create subdomain from company name
            const subdomain = company.name
                .toLowerCase()
                .trim()
                .replace(/[æ]/g, 'ae')
                .replace(/[ø]/g, 'oe')
                .replace(/[å]/g, 'aa')
                .replace(/[^a-z0-9]/g, '') // Fjerner alle ikke-alfanumeriske tegn (inkl. bindestreger)
                .replace(/\s+/g, ''); // Fjerner alle mellemrum

            // Include subdomain in the data being sent
            await onConfirm({
                ...company,
                companyEmail,
                password,
                existingWebsite: websiteInfo,
                customDomain: customDomain, // Tilføj dette
                domain: {
                    subdomain: subdomain
                }
            });

            // Get the current hostname
            const hostname = window.location.hostname;
            const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

            // Construct redirect URL based on environment
            const redirectUrl = isLocalhost
                ? `/${company.cvr}?newsite=true`
                : `https://${subdomain}.xn--hndvrkernet-x8aq.dk?newsite=true`;
                
            window.location.href = redirectUrl;
        } catch (error) {
            // Handle Firebase auth errors
            if (error.code === 'auth/email-already-in-use') {
                setError('Denne email er allerede registreret. Brug venligst en anden email.');
            } else {
                setError(error.message || 'Der opstod en fejl. Prøv igen.');
            }
        }
    };

    const nextStep = () => {
        if (isCorrectWebsite === null && !hasNoWebsite && company.existingWebsite) {
            setError('Vælg venligst om det er jeres eksisterende hjemmeside');
            return;
        }
        setCurrentStep(2);
    };

    return (
        <div className="mt-8 bg-white shadow rounded-lg p-6">
            {loading && (
                <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white p-8 rounded-lg shadow-xl max-w-sm w-full mx-4">
                        <div className="flex flex-col items-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-dark mb-4"></div>
                            <h3 className="text-lg font-semibold font-montserrat text-gray-900 mb-2">
                                Opsætter din hjemmeside
                            </h3>
                            <p className="text-gray-600 text-center">
                                Vi er ved at generere indhold og sætte din nye hjemmeside op. 
                                Dette tager kun et øjeblik...
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <h3 className="text-lg font-medium font-montserrat mb-4">
                {currentStep === 1 ? 'Bekræft firma oplysninger' : 'Opret login'}
            </h3>

            {currentStep === 1 ? (
                <>
                    <div className="space-y-3">
                        <p><span className="font-medium">CVR:</span> {company.cvr}</p>
                        <p><span className="font-medium">Navn:</span> {company.name}</p>
                        
                        {company.existingWebsite && (
                            <div className="mt-4 p-4 bg-brand-light/10 rounded-md">
                                <h4 className="text-sm font-medium text-green-800 mb-2">
                                    Vi fandt denne hjemmeside:
                                </h4>
                                <a 
                                    href={company.existingWebsite.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-brand-dark hover:underline block mb-2"
                                >
                                    {decodePunycode(company.existingWebsite.url)}
                                </a>

                                <div className="mt-3">
                                    <p className="text-sm text-gray-700 mb-2">Er det din eksisterende hjemmeside?</p>
                                    <div className="flex space-x-4 mb-3">
                                        <button
                                            onClick={() => {
                                                setIsCorrectWebsite(true);
                                                setHasNoWebsite(false);
                                            }}
                                            className={`px-3 py-1 rounded ${isCorrectWebsite ? 'bg-brand-dark text-white' : 'bg-gray-100'}`}
                                        >
                                            Ja
                                        </button>
                                        <button
                                            onClick={() => {
                                                setIsCorrectWebsite(false);
                                                setCustomDomain('');
                                            }}
                                            className={`px-3 py-1 rounded ${!isCorrectWebsite ? 'bg-brand-dark text-white' : 'bg-gray-100'}`}
                                        >
                                            Nej
                                        </button>
                                    </div>

                                    {!isCorrectWebsite && (
                                        <div className="mt-3 space-y-3">
                                            {!hasNoWebsite && (
                                                <div>
                                                    <label htmlFor="customDomain" className="block text-sm text-gray-700 mb-1">
                                                        Indtast jeres rigtige hjemmeside:
                                                    </label>
                                                    <input
                                                        type="url"
                                                        id="customDomain"
                                                        value={customDomain}
                                                        onChange={handleUrlChange}
                                                        placeholder="www.minhjemmeside.dk"
                                                        className={`w-full px-3 py-2 border rounded-md text-sm ${
                                                            urlError ? 'border-red-500' : 'border-gray-300'
                                                        }`}
                                                    />
                                                    {urlError && (
                                                        <p className="mt-1 text-sm text-red-600">
                                                            {urlError}
                                                        </p>
                                                    )}
                                                </div>
                                            )}

                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id="noWebsite"
                                                    checked={hasNoWebsite}
                                                    onChange={(e) => {
                                                        setHasNoWebsite(e.target.checked);
                                                        if (e.target.checked) {
                                                            setCustomDomain('');
                                                        }
                                                    }}
                                                    className="h-4 w-4 text-brand-light border-gray-300 rounded"
                                                />
                                                <label htmlFor="noWebsite" className="ml-2 text-sm text-gray-700">
                                                    Vi har ingen hjemmeside
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end space-x-4 mt-6">
                        <button
                            onClick={onEdit}
                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                        >
                            Tilbage
                        </button>
                        <button
                            onClick={nextStep}
                            className="px-6 py-2 bg-brand-dark text-white rounded-md hover:bg-brand-dark/90 transition-colors duration-200"
                        >
                            Næste
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <div className="mt-6 space-y-4">
                        <div className="flex items-center">
                            <h4 className="text-sm font-medium text-gray-900">Opret login</h4>
                            <div className="ml-2 relative">
                                <button 
                                    type="button"
                                    className="text-gray-400 hover:text-gray-500 focus:outline-none group"
                                    aria-label="Information om login"
                                >
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                    <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity absolute left-0 mt-2 w-64 bg-white border border-brand-light p-4 rounded-md shadow-lg z-10">
                                        <h5 className="text-sm font-medium text-gray-900 mb-2">Hvorfor oprette en bruger?</h5>
                                        <p className="text-sm text-gray-600">
                                            Med en brugerkonto kan du senere logge ind og redigere din hjemmesides indhold, 
                                            uploade billeder, ændre kontaktoplysninger og meget mere!
                                        </p>
                                    </div>
                                </button>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="username" className="block text-sm text-gray-700 mb-1">
                                Firma email
                            </label>
                            <input
                                type="email"
                                id="username"
                                value={companyEmail}
                                onChange={(e) => setCompanyEmail(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-500"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                Dette er dit brugernavn
                            </p>
                        </div>

                        {/* ... rest of the login form ... */}
                        <div>
                            <label htmlFor="password" className="block text-sm text-gray-700 mb-1">
                                Password
                            </label>
                            <input
                                type="password"
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className={`w-full px-3 py-2 border rounded-md ${
                                    passwordError ? 'border-red-500' : 'border-gray-300'
                                }`}
                                minLength={6}
                                required
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                Password skal være mindst 6 tegn langt
                            </p>
                        </div>
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm text-gray-700 mb-1">
                                Gentag password
                            </label>
                            <input
                                type="password"
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className={`w-full px-3 py-2 border rounded-md ${
                                    passwordError ? 'border-red-500' : 'border-gray-300'
                                }`}
                                minLength={6}
                                required
                            />
                        </div>
                        {passwordError && (
                            <p className="text-sm text-red-600">{passwordError}</p>
                        )}
                        {error && (
                            <p className="text-sm text-red-600">{error}</p>
                        )}
                        <div className="bg-brand-light/10 p-4 rounded-md">
                            <h5 className="text-sm font-medium text-brand-dark mb-2">Password krav:</h5>
                            <ul className="text-sm text-brand-dark/80 space-y-1">
                                <li>• Minimum 6 tegn</li>
                                <li>• De to passwords skal være ens</li>
                            </ul>
                        </div>
                    </div>

                    <div className="mt-6">
                        <div className="flex items-start">
                            <div className="flex items-center h-5">
                                <input
                                    id="policy"
                                    type="checkbox"
                                    checked={acceptedPolicy}
                                    onChange={(e) => setAcceptedPolicy(e.target.checked)}
                                    className="h-4 w-4 text-brand-light border-gray-300 rounded"
                                />
                            </div>
                            <div className="ml-3 text-sm">
                                <label htmlFor="policy" className="text-gray-700">
                                    Jeg accepterer{' '}
                                    <Link 
                                        to="/privacy" 
                                        target="_blank"
                                        className="text-brand-dark hover:text-brand-dark/90 underline"
                                    >
                                        privatlivspolitikken
                                    </Link>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-4 mt-6">
                        <button
                            onClick={() => setCurrentStep(1)}
                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                        >
                            Tilbage
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={loading}
                            className="px-6 py-2 bg-brand-dark text-white rounded-md hover:bg-brand-dark/90 transition-colors duration-200"
                        >
                            {loading ? 'Opretter...' : 'Opret hjemmeside'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default CompanyPreview;